import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { ChevronLeft, ChevronRight, DollarSign, Calendar, Car, Check, Search, ArrowLeft, Filter, Send, User, Mail, Phone, MessageSquare, CheckCircle, ArrowUp, ArrowDown, SlidersHorizontal } from 'lucide-react';
import { carData, CarModel, CarType, TrimLevel, getCarImageUrl, getYearsFromRange, estimatePriceForYear, estimateTrimPriceForYear, calculateTotalCost } from '../data/carData';
import { DualRangeSlider } from '../components/ui/DualRangeSlider';
import { useUserRequests } from '../hooks/useUserRequests';
import { Footer } from '../components/Footer';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../hooks/useAuth';
interface CarFinderPageProps {
  onBack: () => void;
  onNavigate: (page: string) => void;
}
type Step = 'budget' | 'year' | 'type' | 'makes' | 'results' | 'selectYear' | 'trim' | 'contact';
interface FilterState {
  budgetMin: number;
  budgetMax: number;
  yearMin: number;
  yearMax: number;
  types: CarType[];
  makes: string[];
}
interface ContactFormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}
type SortOption = 'price' | 'year';
type SortDirection = 'asc' | 'desc';
const CURRENT_YEAR = new Date().getFullYear();
const MIN_LEGAL_YEAR = CURRENT_YEAR - 8;
const MAX_BUDGET_LIMIT = 40000;
const MIN_BUDGET_LIMIT = 3000;
const INITIAL_FILTERS: FilterState = {
  budgetMin: 5000,
  budgetMax: 15000,
  yearMin: MIN_LEGAL_YEAR,
  yearMax: CURRENT_YEAR,
  types: [],
  makes: []
};
const INITIAL_CONTACT_FORM: ContactFormState = {
  name: '',
  email: '',
  phone: '',
  message: ''
};
const TYPE_OPTIONS: CarType[] = ['Sedán', 'SUV', 'Pick-up', 'Hatchback', 'Deportivo'];
const MAKE_OPTIONS = Array.from(new Set(carData.map(c => c.make))).sort();
const EMAILJS_SERVICE_ID = 'service_m7m0m09';
const EMAILJS_TEMPLATE_ID = 'template_hyqrklh';
const EMAILJS_PUBLIC_KEY = 'w8t9KyY5LzV6mitFs';
export function CarFinderPage({
  onBack,
  onNavigate
}: CarFinderPageProps) {
  const [step, setStep] = useState<Step>('budget');
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [results, setResults] = useState<CarModel[]>([]);
  const [direction, setDirection] = useState(0);
  const {
    saveRequest
  } = useUserRequests();
  const {
    user
  } = useAuth();
  // Selection state
  const [selectedCar, setSelectedCar] = useState<CarModel | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedTrim, setSelectedTrim] = useState<TrimLevel | null>(null);
  // Contact form state
  const [contactForm, setContactForm] = useState<ContactFormState>(INITIAL_CONTACT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Sorting state
  const [sortBy, setSortBy] = useState<SortOption>('price');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const handleImageError = (key: string) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Filter and Sort Logic
  useEffect(() => {
    if (step === 'results') {
      let filtered = carData.filter(car => {
        // Budget check: car's cheapest option must be within user's max budget
        // and car's most expensive option must be at or above user's min budget
        const fitsbudget = car.priceRange.min <= filters.budgetMax && car.priceRange.max >= filters.budgetMin;
        // Year overlap check
        const parts = car.yearRange.split('-').map(Number);
        const carStart = parts[0];
        const carEnd = parts[1] || parts[0];
        const yearOverlap = carStart <= filters.yearMax && carEnd >= filters.yearMin;
        const fitsType = filters.types.length === 0 || filters.types.includes(car.type);
        const fitsMake = filters.makes.length === 0 || filters.makes.includes(car.make);
        return fitsbudget && yearOverlap && fitsType && fitsMake;
      });
      // Sorting
      filtered.sort((a, b) => {
        let valA = 0;
        let valB = 0;
        if (sortBy === 'price') {
          valA = (a.priceRange.min + a.priceRange.max) / 2;
          valB = (b.priceRange.min + b.priceRange.max) / 2;
        } else if (sortBy === 'year') {
          const partsA = a.yearRange.split('-').map(Number);
          const partsB = b.yearRange.split('-').map(Number);
          valA = partsA[1] || partsA[0];
          valB = partsB[1] || partsB[0];
        }
        if (sortDirection === 'asc') {
          return valA - valB;
        } else {
          return valB - valA;
        }
      });
      setResults(filtered);
    }
  }, [step, filters, sortBy, sortDirection]);
  const nextStep = () => {
    setDirection(1);
    if (step === 'budget') setStep('year');else if (step === 'year') setStep('type');else if (step === 'type') setStep('makes');else if (step === 'makes') setStep('results');else if (step === 'results' && selectedCar) setStep('selectYear');else if (step === 'selectYear' && selectedYear) setStep('trim');else if (step === 'trim' && selectedTrim) setStep('contact');
  };
  const prevStep = () => {
    setDirection(-1);
    if (step === 'year') setStep('budget');else if (step === 'type') setStep('year');else if (step === 'makes') setStep('type');else if (step === 'results') setStep('makes');else if (step === 'selectYear') {
      setStep('results');
      setSelectedYear(null);
    } else if (step === 'trim') setStep('selectYear');else if (step === 'contact') setStep('trim');
  };
  const toggleType = (type: CarType) => {
    setFilters(prev => ({
      ...prev,
      types: prev.types.includes(type) ? prev.types.filter(t => t !== type) : [...prev.types, type]
    }));
  };
  const toggleMake = (make: string) => {
    setFilters(prev => ({
      ...prev,
      makes: prev.makes.includes(make) ? prev.makes.filter(m => m !== make) : [...prev.makes, make]
    }));
  };
  const handleSelectCar = (car: CarModel) => {
    setSelectedCar(car);
    setSelectedYear(null);
    setSelectedTrim(null);
    setDirection(1);
    setStep('selectYear');
    window.scrollTo(0, 0);
  };
  const handleSelectYear = (year: number) => {
    setSelectedYear(year);
    setDirection(1);
    setStep('trim');
    window.scrollTo(0, 0);
  };
  const handleSelectTrim = (trim: TrimLevel) => {
    setSelectedTrim(trim);
    setDirection(1);
    setStep('contact');
    window.scrollTo(0, 0);
  };
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCar || !selectedTrim || !selectedYear) return;
    // If not logged in, redirect to auth (or handle as guest if you change RLS)
    if (!user) {
      // Optional: Save state to localStorage to restore after login
      alert('Para dar seguimiento a tu solicitud, por favor inicia sesión o regístrate.');
      onNavigate('auth');
      return;
    }
    setIsSubmitting(true);
    try {
      const yearAdjustedPrice = estimateTrimPriceForYear(selectedCar, selectedTrim, selectedYear);
      const yearPriceStr = `$${yearAdjustedPrice.min.toLocaleString()} - $${yearAdjustedPrice.max.toLocaleString()}`;
      // Save request to Supabase
      await saveRequest({
        car: {
          year: selectedYear,
          make: selectedCar.make,
          model: selectedCar.model,
          trim: selectedTrim.name,
          image: getCarImageUrl(selectedCar.make, selectedCar.model, selectedYear)
        },
        priceRange: {
          min: yearAdjustedPrice.min,
          max: yearAdjustedPrice.max
        }
      }, contactForm);
      // Send email notification (keep existing emailjs for immediate admin notification)
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_name: contactForm.name,
        from_email: contactForm.email,
        phone: contactForm.phone,
        message: `
Vehículo de interés: ${selectedYear} ${selectedCar.make} ${selectedCar.model}
Versión seleccionada: ${selectedTrim.name}
Rango de precio estimado (${selectedYear}): ${yearPriceStr}

Mensaje del cliente:
${contactForm.message || 'Sin mensaje adicional'}
          `,
        vehicle_types: `${selectedYear} ${selectedCar.make} ${selectedCar.model} - ${selectedTrim.name}`,
        budget: yearPriceStr,
        usage: 'Interés específico desde Car Finder'
      }, EMAILJS_PUBLIC_KEY);
      setIsSuccess(true);
    } catch (error) {
      console.error('EmailJS error:', error);
      alert('Hubo un error al enviar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const allSteps: Step[] = ['budget', 'year', 'type', 'makes', 'results', 'selectYear', 'trim', 'contact'];
  const renderProgressBar = () => {
    const currentIndex = allSteps.indexOf(step);
    const progress = (currentIndex + 1) / allSteps.length * 100;
    return <div className="w-full bg-gray-200 h-2 rounded-full mb-8 overflow-hidden" data-id="element-597">
        <motion.div className="h-full bg-blue-600" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-598" />
      </div>;
  };
  const getBudgetDescription = (min: number, max: number) => {
    if (max < 8000) return 'Autos económicos, proyectos o primeros vehículos.';
    if (max < 15000) return 'Sedanes confiables y SUVs de años anteriores.';
    if (max < 25000) return 'Modelos recientes, tecnología moderna y mayor seguridad.';
    return 'Vehículos premium, camionetas recientes o deportivos.';
  };
  const renderCarImage = (make: string, model: string, year: number, imageKey: string, className: string) => {
    if (failedImages.has(imageKey)) {
      return <div className={`${className} bg-gray-100 flex items-center justify-center`} data-id="element-599">
          <Car className="w-12 h-12 text-gray-300" data-id="element-600" />
        </div>;
    }
    return <img src={getCarImageUrl(make, model, year)} alt={`${year} ${make} ${model}`} className={`${className} object-cover bg-gray-100`} onError={() => handleImageError(imageKey)} data-id="element-601" />;
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-602">
        <Navbar onNavigate={onNavigate} data-id="element-603" />
        <div className="flex-1 pt-20 pb-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center" data-id="element-604">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-10 text-center border border-gray-100" data-id="element-605">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-606">
              <CheckCircle className="w-10 h-10 text-green-600" data-id="element-607" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-608">
              ¡Solicitud Enviada!
            </h2>
            <p className="text-gray-600 text-lg mb-8" data-id="element-609">
              Hemos recibido tu interés por el{' '}
              <strong data-id="element-610">
                {selectedYear} {selectedCar?.make} {selectedCar?.model}{' '}
                {selectedTrim?.name}
              </strong>
              . Te contactaré pronto para asesorarte con la importación.
            </p>
            <button onClick={() => {
            setIsSuccess(false);
            setStep('budget');
            setFilters(INITIAL_FILTERS);
            setSelectedCar(null);
            setSelectedYear(null);
            setSelectedTrim(null);
            setContactForm(INITIAL_CONTACT_FORM);
          }} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg transition-all" data-id="element-611">
              Buscar otro vehículo
            </button>
          </div>
        </div>
        <Footer data-id="element-612" />
      </div>;
  }
  const genericHeaderSteps: Step[] = ['budget', 'year', 'type', 'makes'];
  const showGenericHeader = genericHeaderSteps.includes(step);
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-613">
      <Navbar onNavigate={onNavigate} data-id="element-614" />
      <div className="flex-1 pt-20 pb-12 px-4 sm:px-6 lg:px-8" data-id="element-615">
        <div className="max-w-3xl mx-auto" data-id="element-616">
          <div className="flex items-center mb-8" data-id="element-617">
            <button onClick={onBack} className="flex items-center text-gray-600 hover:text-blue-600 transition-colors" data-id="element-618">
              <ArrowLeft className="w-5 h-5 mr-2" data-id="element-619" />
              Volver al inicio
            </button>
          </div>

          {(step === 'year' || step === 'results') && <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-id="element-620">
              <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-621" />
              <div data-id="element-622">
                <p className="text-sm font-medium text-blue-900" data-id="element-623">
                  Ley de importación de El Salvador (8 años)
                </p>
                <p className="text-xs text-blue-700 mt-1" data-id="element-624">
                  Solo se pueden importar vehículos con un máximo de 8 años de
                  antigüedad. En {CURRENT_YEAR}, el año mínimo permitido es{' '}
                  <strong data-id="element-625">{MIN_LEGAL_YEAR}</strong>.
                </p>
              </div>
            </div>}

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100" data-id="element-626">
            <div className="p-6 md:p-10" data-id="element-627">
              {showGenericHeader && <div className="mb-8" data-id="element-628">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-629">
                    Encuentra tu Carro Ideal
                  </h1>
                  <p className="text-gray-500" data-id="element-630">
                    Responde unas preguntas para encontrar las mejores opciones
                    para ti.
                  </p>
                  <div className="mt-6" data-id="element-631">{renderProgressBar()}</div>
                </div>}

              {step === 'selectYear' && selectedCar && <div className="mb-8" data-id="element-632">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-633">
                    Selecciona el año del {selectedCar.make} {selectedCar.model}
                  </h1>
                  <p className="text-gray-500" data-id="element-634">
                    El precio varía según el año del modelo. Los más recientes
                    tienen mayor valor.
                  </p>
                  <div className="mt-6" data-id="element-635">{renderProgressBar()}</div>
                </div>}

              {step === 'trim' && selectedCar && <div className="mb-8" data-id="element-636">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-637">
                    Elige la versión del {selectedYear} {selectedCar.make}{' '}
                    {selectedCar.model}
                  </h1>
                  <p className="text-gray-500" data-id="element-638">
                    Precios ajustados al año <strong data-id="element-639">{selectedYear}</strong>.
                    Los colores indican qué tan bien encaja con tu presupuesto.
                  </p>
                  <div className="mt-6" data-id="element-640">{renderProgressBar()}</div>
                </div>}

              {step === 'contact' && selectedCar && selectedTrim && <div className="mb-8" data-id="element-641">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-642">
                    ¡Excelente elección!
                  </h1>
                  <p className="text-gray-500" data-id="element-643">
                    Déjanos tus datos para enviarte una cotización detallada del{' '}
                    <strong data-id="element-644">
                      {selectedYear} {selectedCar.make} {selectedCar.model}{' '}
                      {selectedTrim.name}
                    </strong>
                    .
                  </p>
                  <div className="mt-6" data-id="element-645">{renderProgressBar()}</div>
                </div>}

              <AnimatePresence custom={direction} mode="wait" data-id="element-646">
                <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30
              }} data-id="element-647">
                  {/* STEP 1: BUDGET */}
                  {step === 'budget' && <div className="space-y-8" data-id="element-648">
                      <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-649">
                        <DollarSign className="text-blue-600" data-id="element-650" />
                        ¿Cuál es tu rango de presupuesto?
                      </h2>
                      <p className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg border border-blue-100" data-id="element-651">
                        Incluye costo del auto, importación, impuestos,
                        reparaciones y costos de circulación
                      </p>
                      <div className="px-4 py-8" data-id="element-652">
                        <div className="text-center mb-8" data-id="element-653">
                          <span className="text-4xl font-bold text-blue-600" data-id="element-654">
                            ${filters.budgetMin.toLocaleString()} - $
                            {filters.budgetMax.toLocaleString()}
                          </span>
                        </div>

                        <DualRangeSlider min={MIN_BUDGET_LIMIT} max={MAX_BUDGET_LIMIT} step={100} value={[filters.budgetMin, filters.budgetMax]} onChange={([min, max]) => setFilters(prev => ({
                      ...prev,
                      budgetMin: min,
                      budgetMax: max
                    }))} formatLabel={val => `$${val.toLocaleString()}`} inputPrefix="$" data-id="element-655" />

                        <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200 text-center" data-id="element-656">
                          <p className="text-gray-700 font-medium" data-id="element-657">
                            {getBudgetDescription(filters.budgetMin, filters.budgetMax)}
                          </p>
                        </div>
                      </div>
                    </div>}

                  {/* STEP 2: YEAR */}
                  {step === 'year' && <div className="space-y-8" data-id="element-658">
                      <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-659">
                        <Calendar className="text-blue-600" data-id="element-660" />
                        ¿Qué rango de años buscas?
                      </h2>
                      <p className="text-sm text-gray-500 bg-amber-50 p-3 rounded-lg border border-amber-200" data-id="element-661">
                        Recuerda: Solo se permiten vehículos de {MIN_LEGAL_YEAR}{' '}
                        en adelante.
                      </p>

                      <div className="px-4 py-8" data-id="element-662">
                        <div className="text-center mb-8" data-id="element-663">
                          <span className="text-4xl font-bold text-blue-600" data-id="element-664">
                            {filters.yearMin} - {filters.yearMax}
                          </span>
                        </div>

                        <DualRangeSlider min={MIN_LEGAL_YEAR} max={CURRENT_YEAR} step={1} value={[filters.yearMin, filters.yearMax]} onChange={([min, max]) => setFilters(prev => ({
                      ...prev,
                      yearMin: min,
                      yearMax: max
                    }))} formatLabel={val => val.toString()} data-id="element-665" />
                      </div>
                    </div>}

                  {/* STEP 3: TYPE */}
                  {step === 'type' && <div className="space-y-6" data-id="element-666">
                      <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-667">
                        <Car className="text-blue-600" data-id="element-668" />
                        ¿Qué tipo de vehículo prefieres?
                      </h2>
                      <p className="text-sm text-gray-500" data-id="element-669">
                        Puedes seleccionar varios.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-670">
                        {TYPE_OPTIONS.map(type => <button key={type} onClick={() => toggleType(type)} className={`p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${filters.types.includes(type) ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`} data-id="element-671">
                            <span className={`font-medium ${filters.types.includes(type) ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-672">
                              {type}
                            </span>
                            {filters.types.includes(type) && <Check className="text-blue-600 w-5 h-5" data-id="element-673" />}
                          </button>)}
                      </div>
                    </div>}

                  {/* STEP 4: MAKES */}
                  {step === 'makes' && <div className="space-y-6" data-id="element-674">
                      <h2 className="text-xl font-semibold flex items-center gap-2" data-id="element-675">
                        <Filter className="text-blue-600" data-id="element-676" />
                        ¿Marcas preferidas?
                      </h2>
                      <p className="text-sm text-gray-500" data-id="element-677">
                        Selecciona tus favoritas o déjalo vacío para ver todas.
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-678">
                        {MAKE_OPTIONS.map(make => <button key={make} onClick={() => toggleMake(make)} className={`py-3 px-4 rounded-xl border-2 transition-all text-sm font-medium ${filters.makes.includes(make) ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-md' : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-gray-50'}`} data-id="element-679">
                            {make}
                          </button>)}
                      </div>
                    </div>}

                  {/* STEP 5: RESULTS */}
                  {step === 'results' && <div className="space-y-6" data-id="element-680">
                      <div className="text-center" data-id="element-681">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-682">
                          Resultados Encontrados
                        </h2>
                        <p className="text-gray-600" data-id="element-683">
                          {results.length} vehículos coinciden con tu búsqueda.
                        </p>
                      </div>

                      {/* Sorting and Filtering Controls */}
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4" data-id="element-684">
                        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-685">
                          <div className="flex items-center gap-2" data-id="element-686">
                            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showFilters ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-687">
                              <SlidersHorizontal className="w-4 h-4" data-id="element-688" />
                              Filtros
                            </button>
                          </div>

                          <div className="flex items-center gap-2" data-id="element-689">
                            <span className="text-sm text-gray-500 hidden sm:inline" data-id="element-690">
                              Ordenar por:
                            </span>
                            <div className="flex bg-white rounded-lg border border-gray-300 p-1" data-id="element-691">
                              <button onClick={() => setSortBy('price')} className={`px-3 py-1 rounded text-sm font-medium transition-colors ${sortBy === 'price' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-692">
                                Precio
                              </button>
                              <button onClick={() => setSortBy('year')} className={`px-3 py-1 rounded text-sm font-medium transition-colors ${sortBy === 'year' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-693">
                                Año
                              </button>
                            </div>
                            <button onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600" title={sortDirection === 'asc' ? 'Ascendente' : 'Descendente'} data-id="element-694">
                              {sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" data-id="element-695" /> : <ArrowDown className="w-4 h-4" data-id="element-696" />}
                            </button>
                          </div>
                        </div>

                        {/* Collapsible Filters */}
                        <AnimatePresence data-id="element-697">
                          {showFilters && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden" data-id="element-698">
                              <div className="pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-699">
                                <div data-id="element-700">
                                  <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-701">
                                    Rango de Precio: $
                                    {filters.budgetMin.toLocaleString()} - $
                                    {filters.budgetMax.toLocaleString()}
                                  </label>
                                  <DualRangeSlider min={MIN_BUDGET_LIMIT} max={MAX_BUDGET_LIMIT} step={100} value={[filters.budgetMin, filters.budgetMax]} onChange={([min, max]) => setFilters(prev => ({
                              ...prev,
                              budgetMin: min,
                              budgetMax: max
                            }))} formatLabel={val => `$${(val / 1000).toFixed(0)}k`} inputPrefix="$" data-id="element-702" />
                                </div>
                                <div data-id="element-703">
                                  <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-704">
                                    Rango de Años: {filters.yearMin} -{' '}
                                    {filters.yearMax}
                                  </label>
                                  <DualRangeSlider min={MIN_LEGAL_YEAR} max={CURRENT_YEAR} step={1} value={[filters.yearMin, filters.yearMax]} onChange={([min, max]) => setFilters(prev => ({
                              ...prev,
                              yearMin: min,
                              yearMax: max
                            }))} formatLabel={val => val.toString()} data-id="element-705" />
                                </div>

                                {/* Type filter */}
                                <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-706">
                                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-707">
                                    <Car className="w-4 h-4 inline mr-1.5 -mt-0.5" data-id="element-708" />
                                    Tipo de Vehículo
                                    {filters.types.length > 0 && <span className="ml-2 text-xs text-blue-600 font-normal" data-id="element-709">
                                        ({filters.types.length} seleccionado
                                        {filters.types.length > 1 ? 's' : ''})
                                      </span>}
                                  </label>
                                  <div className="flex flex-wrap gap-2" data-id="element-710">
                                    {TYPE_OPTIONS.map(type => <button key={type} onClick={() => toggleType(type)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filters.types.includes(type) ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-300 hover:bg-blue-50'}`} data-id="element-711">
                                        {type}
                                      </button>)}
                                    {filters.types.length > 0 && <button onClick={() => setFilters(prev => ({
                                ...prev,
                                types: []
                              }))} className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-712">
                                        Limpiar
                                      </button>}
                                  </div>
                                </div>

                                {/* Make filter */}
                                <div className="mt-5 pt-4 border-t border-gray-200" data-id="element-713">
                                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-714">
                                    <Filter className="w-4 h-4 inline mr-1.5 -mt-0.5" data-id="element-715" />
                                    Marca
                                    {filters.makes.length > 0 && <span className="ml-2 text-xs text-blue-600 font-normal" data-id="element-716">
                                        ({filters.makes.length} seleccionada
                                        {filters.makes.length > 1 ? 's' : ''})
                                      </span>}
                                  </label>
                                  <div className="flex flex-wrap gap-2" data-id="element-717">
                                    {MAKE_OPTIONS.map(make => <button key={make} onClick={() => toggleMake(make)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filters.makes.includes(make) ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-300 hover:bg-blue-50'}`} data-id="element-718">
                                        {make}
                                      </button>)}
                                    {filters.makes.length > 0 && <button onClick={() => setFilters(prev => ({
                                ...prev,
                                makes: []
                              }))} className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-719">
                                        Limpiar
                                      </button>}
                                  </div>
                                </div>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>

                      {results.length > 0 ? <div className="grid grid-cols-1 gap-6" data-id="element-720">
                          {results.map(car => {
                      const years = getYearsFromRange(car.yearRange);
                      const representativeYear = years[Math.floor(years.length / 2)];
                      // Clamp displayed price range to user's budget
                      const displayMin = Math.max(car.priceRange.min, filters.budgetMin);
                      const displayMax = Math.min(car.priceRange.max, filters.budgetMax);
                      return <div key={car.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col sm:flex-row" data-id="element-721">
                                <div className="sm:w-64 h-48 sm:h-auto relative flex-shrink-0 overflow-hidden" data-id="element-722">
                                  {renderCarImage(car.make, car.model, representativeYear, car.id, 'w-full h-full')}
                                  <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm font-medium" data-id="element-723">
                                    {car.yearRange}
                                  </div>
                                </div>
                                <div className="p-4 sm:p-6 flex-1 flex flex-col justify-between" data-id="element-724">
                                  <div data-id="element-725">
                                    <div className="flex justify-between items-start mb-2 gap-2" data-id="element-726">
                                      <div data-id="element-727">
                                        <h3 className="text-lg sm:text-xl font-bold text-gray-900" data-id="element-728">
                                          {car.make} {car.model}
                                        </h3>
                                        <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium mt-1" data-id="element-729">
                                          {car.type}
                                        </span>
                                      </div>
                                      <div className="text-right flex-shrink-0" data-id="element-730">
                                        <p className="text-xs sm:text-sm text-gray-500" data-id="element-731">
                                          Rango estimado
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-green-600" data-id="element-732">
                                          ${displayMin.toLocaleString()} - $
                                          {displayMax.toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                    <p className="text-gray-600 text-sm mb-4 hidden sm:block" data-id="element-733">
                                      {car.description}
                                    </p>
                                    <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-734">
                                      <span className="flex items-center gap-1" data-id="element-735">
                                        <Calendar className="w-4 h-4" data-id="element-736" />{' '}
                                        {car.yearRange}
                                      </span>
                                      <span className="text-gray-400" data-id="element-737">·</span>
                                      <span data-id="element-738">{car.engine}</span>
                                    </div>
                                  </div>
                                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end" data-id="element-739">
                                    <button onClick={() => handleSelectCar(car)} className="text-blue-600 font-bold hover:text-blue-800 text-sm flex items-center gap-1" data-id="element-740">
                                      Cotizar este modelo{' '}
                                      <ChevronRight className="w-4 h-4" data-id="element-741" />
                                    </button>
                                  </div>
                                </div>
                              </div>;
                    })}
                        </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300" data-id="element-742">
                          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-743" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-744">
                            No encontramos coincidencias exactas
                          </h3>
                          <p className="text-gray-500 mb-6" data-id="element-745">
                            Intenta ajustar tu presupuesto o ser menos
                            específico con los filtros.
                          </p>
                          <button onClick={() => {
                      setFilters(INITIAL_FILTERS);
                      setStep('budget');
                    }} className="text-blue-600 font-bold hover:underline" data-id="element-746">
                            Reiniciar búsqueda
                          </button>
                        </div>}
                    </div>}

                  {/* STEP 5.5: SELECT YEAR */}
                  {step === 'selectYear' && selectedCar && <div className="space-y-6" data-id="element-747">
                      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200" data-id="element-748">
                        <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0" data-id="element-749">
                          {renderCarImage(selectedCar.make, selectedCar.model, getYearsFromRange(selectedCar.yearRange)[Math.floor(getYearsFromRange(selectedCar.yearRange).length / 2)], `preview-${selectedCar.id}`, 'w-full h-full')}
                        </div>
                        <div data-id="element-750">
                          <h3 className="font-bold text-gray-900" data-id="element-751">
                            {selectedCar.make} {selectedCar.model}
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-752">
                            {selectedCar.type} · {selectedCar.yearRange}
                          </p>
                        </div>
                      </div>

                      {/* Traffic light legend */}
                      <div className="flex flex-wrap gap-3 text-xs" data-id="element-753">
                        <div className="flex items-center gap-1.5" data-id="element-754">
                          <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-755"></div>
                          <span className="text-gray-600" data-id="element-756">
                            Dentro de presupuesto
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-757">
                          <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-758"></div>
                          <span className="text-gray-600" data-id="element-759">
                            Cerca del límite
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-760">
                          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-761"></div>
                          <span className="text-gray-600" data-id="element-762">
                            Sobre presupuesto
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-763">
                        {getYearsFromRange(selectedCar.yearRange).filter(yr => yr >= MIN_LEGAL_YEAR).reverse().map(yr => {
                      const priceEst = estimatePriceForYear(selectedCar, yr);
                      // Traffic light: green if max fits, amber if partially, red if over
                      let status: 'green' | 'amber' | 'red';
                      if (priceEst.max <= filters.budgetMax) {
                        status = 'green';
                      } else if (priceEst.min <= filters.budgetMax) {
                        status = 'amber';
                      } else {
                        status = 'red';
                      }
                      // Don't show years that are way over budget (> +$2000)
                      if (priceEst.min > filters.budgetMax + 2000) return null;
                      const statusStyles = {
                        green: {
                          border: 'border-green-200 hover:border-green-500',
                          bg: 'bg-white hover:bg-green-50/30',
                          dot: 'bg-green-500',
                          price: 'text-green-600',
                          year: 'text-gray-900 group-hover:text-green-700',
                          chevron: 'text-gray-400 group-hover:text-green-600'
                        },
                        amber: {
                          border: 'border-amber-200 hover:border-amber-500',
                          bg: 'bg-amber-50/30 hover:bg-amber-50/50',
                          dot: 'bg-amber-500',
                          price: 'text-amber-600',
                          year: 'text-amber-900 group-hover:text-amber-700',
                          chevron: 'text-amber-400 group-hover:text-amber-600'
                        },
                        red: {
                          border: 'border-red-200 hover:border-red-400',
                          bg: 'bg-red-50/20 hover:bg-red-50/40',
                          dot: 'bg-red-400',
                          price: 'text-red-500',
                          year: 'text-red-900/70 group-hover:text-red-700',
                          chevron: 'text-red-300 group-hover:text-red-500'
                        }
                      };
                      const s = statusStyles[status];
                      return <button key={yr} onClick={() => handleSelectYear(yr)} className={`group relative flex items-center p-5 rounded-xl border-2 transition-all text-left shadow-sm hover:shadow-md ${s.border} ${s.bg}`} data-id="element-764">
                                <div className={`absolute top-3 left-3 w-2.5 h-2.5 rounded-full ${s.dot}`} data-id="element-765"></div>
                                <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 mr-4 ml-2" data-id="element-766">
                                  {renderCarImage(selectedCar.make, selectedCar.model, yr, `year-${selectedCar.id}-${yr}`, 'w-full h-full')}
                                </div>
                                <div className="flex-1" data-id="element-767">
                                  <div className="flex items-center justify-between" data-id="element-768">
                                    <span className={`text-2xl font-bold transition-colors ${s.year}`} data-id="element-769">
                                      {yr}
                                    </span>
                                    <ChevronRight className={`w-5 h-5 ${s.chevron}`} data-id="element-770" />
                                  </div>
                                  <p className={`text-sm font-medium mt-1 ${s.price}`} data-id="element-771">
                                    ${priceEst.min.toLocaleString()} - $
                                    {priceEst.max.toLocaleString()}
                                  </p>
                                  {status === 'red' && <span className="text-[10px] font-semibold uppercase tracking-wider text-red-500" data-id="element-772">
                                      Sobre presupuesto
                                    </span>}
                                </div>
                              </button>;
                    })}
                      </div>
                    </div>}

                  {/* STEP 6: TRIM SELECTION */}
                  {step === 'trim' && selectedCar && selectedYear && <div className="space-y-6" data-id="element-773">
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-start gap-2" data-id="element-774">
                        <DollarSign className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" data-id="element-775" />
                        <p className="text-xs text-gray-600" data-id="element-776">
                          Precios estimados para un{' '}
                          <strong data-id="element-777">
                            {selectedYear} {selectedCar.make}{' '}
                            {selectedCar.model}
                          </strong>{' '}
                          según tu presupuesto de{' '}
                          <strong data-id="element-778">
                            ${filters.budgetMin.toLocaleString()} - $
                            {filters.budgetMax.toLocaleString()}
                          </strong>
                          .
                        </p>
                      </div>

                      {/* Traffic light legend */}
                      <div className="flex flex-wrap gap-3 text-xs" data-id="element-779">
                        <div className="flex items-center gap-1.5" data-id="element-780">
                          <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-781"></div>
                          <span className="text-gray-600" data-id="element-782">
                            Dentro de presupuesto
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-783">
                          <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-784"></div>
                          <span className="text-gray-600" data-id="element-785">
                            Cerca del límite
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-786">
                          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-787"></div>
                          <span className="text-gray-600" data-id="element-788">
                            Sobre presupuesto
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3" data-id="element-789">
                        {selectedCar.trims.map((trim, index) => {
                      // Use year-adjusted prices instead of global trim prices
                      const yearPrice = estimateTrimPriceForYear(selectedCar, trim, selectedYear);
                      const trimMin = yearPrice.min;
                      const trimMax = yearPrice.max;
                      // Traffic light logic based on user's budget range
                      let status: 'green' | 'amber' | 'red';
                      if (trimMax <= filters.budgetMax && trimMin >= filters.budgetMin) {
                        status = 'green';
                      } else if (trimMin <= filters.budgetMax) {
                        status = trimMax <= filters.budgetMax + 1000 ? 'amber' : 'amber';
                        // Green if fully within range
                        if (trimMax <= filters.budgetMax) status = 'green';
                      } else {
                        status = 'red';
                      }
                      // Simplified: green = max fits budget, amber = partially, red = over
                      if (trimMax <= filters.budgetMax) {
                        status = 'green';
                      } else if (trimMin <= filters.budgetMax) {
                        status = 'amber';
                      } else {
                        status = 'red';
                      }
                      const statusStyles = {
                        green: {
                          border: 'border-green-200 hover:border-green-500',
                          bg: 'bg-white hover:bg-green-50/30',
                          dot: 'bg-green-500',
                          price: 'text-green-600 bg-green-50 border-green-100',
                          title: 'text-gray-900 group-hover:text-green-700',
                          chevron: 'bg-green-100 group-hover:bg-green-600',
                          chevronIcon: 'text-green-600 group-hover:text-white',
                          label: '',
                          featureBg: 'bg-gray-100 text-gray-700'
                        },
                        amber: {
                          border: 'border-amber-200 hover:border-amber-500',
                          bg: 'bg-amber-50/30 hover:bg-amber-50/50',
                          dot: 'bg-amber-500',
                          price: 'text-amber-700 bg-amber-50 border-amber-200',
                          title: 'text-amber-900 group-hover:text-amber-700',
                          chevron: 'bg-amber-100 group-hover:bg-amber-500',
                          chevronIcon: 'text-amber-600 group-hover:text-white',
                          label: 'Ajustado',
                          featureBg: 'bg-gray-100 text-gray-700'
                        },
                        red: {
                          border: 'border-red-200 hover:border-red-400',
                          bg: 'bg-red-50/20 hover:bg-red-50/40',
                          dot: 'bg-red-400',
                          price: 'text-red-600 bg-red-50 border-red-200',
                          title: 'text-red-900/70 group-hover:text-red-700',
                          chevron: 'bg-red-100 group-hover:bg-red-500',
                          chevronIcon: 'text-red-500 group-hover:text-white',
                          label: 'Sobre presupuesto',
                          featureBg: 'bg-gray-100/80 text-gray-500'
                        }
                      };
                      const s = statusStyles[status];
                      return <button key={index} onClick={() => handleSelectTrim(trim)} className={`group relative flex flex-col sm:flex-row items-start sm:items-center p-5 rounded-xl border-2 transition-all text-left shadow-sm hover:shadow-md ${s.border} ${s.bg}`} data-id="element-790">
                              {/* Status dot */}
                              <div className={`absolute top-4 left-4 w-2.5 h-2.5 rounded-full ${s.dot}`} data-id="element-791"></div>

                              <div className="flex-1 pl-5" data-id="element-792">
                                <div className="flex items-center justify-between mb-1.5 gap-2" data-id="element-793">
                                  <div className="flex items-center gap-2" data-id="element-794">
                                    <h3 className={`text-lg font-bold transition-colors ${s.title}`} data-id="element-795">
                                      {trim.name}
                                    </h3>
                                    {s.label && <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${status === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`} data-id="element-796">
                                        {s.label}
                                      </span>}
                                  </div>
                                  <span className={`text-sm sm:text-base font-bold px-2.5 py-1 rounded-lg border ${s.price}`} data-id="element-797">
                                    ${trimMin.toLocaleString()} - $
                                    {trimMax.toLocaleString()}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-798">
                                  {trim.features.map((feature, i) => <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.featureBg}`} data-id="element-799">
                                      {feature}
                                    </span>)}
                                </div>
                              </div>
                              <div className="mt-3 sm:mt-0 sm:ml-4" data-id="element-800">
                                <div className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${s.chevron}`} data-id="element-801">
                                  <ChevronRight className={`w-5 h-5 ${s.chevronIcon}`} data-id="element-802" />
                                </div>
                              </div>
                            </button>;
                    })}
                      </div>
                    </div>}

                  {/* STEP 7: CONTACT FORM */}
                  {step === 'contact' && selectedCar && selectedTrim && selectedYear && <div className="space-y-6" data-id="element-803">
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-6" data-id="element-804">
                          <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-3" data-id="element-805">
                            Resumen de tu selección
                          </h3>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-806">
                            <div className="flex items-center gap-4" data-id="element-807">
                              <div className="w-20 h-20 rounded-lg overflow-hidden border border-blue-200 flex-shrink-0" data-id="element-808">
                                {renderCarImage(selectedCar.make, selectedCar.model, selectedYear, `contact-${selectedCar.id}`, 'w-full h-full')}
                              </div>
                              <div data-id="element-809">
                                <p className="text-xl font-bold text-gray-900" data-id="element-810">
                                  {selectedYear} {selectedCar.make}{' '}
                                  {selectedCar.model}
                                </p>
                                <p className="text-gray-600" data-id="element-811">
                                  Versión:{' '}
                                  <span className="font-semibold" data-id="element-812">
                                    {selectedTrim.name}
                                  </span>
                                </p>
                              </div>
                            </div>
                            {(() => {
                        const yearAdjusted = estimateTrimPriceForYear(selectedCar, selectedTrim, selectedYear);
                        return <div className="text-right" data-id="element-813">
                                  <p className="text-sm text-gray-500" data-id="element-814">
                                    Estimado total ({selectedYear})
                                  </p>
                                  <p className="text-xl font-mono font-bold text-green-600" data-id="element-815">
                                    ${yearAdjusted.min.toLocaleString()} - $
                                    {yearAdjusted.max.toLocaleString()}
                                  </p>
                                </div>;
                      })()}
                          </div>
                        </div>

                        <form onSubmit={handleContactSubmit} className="space-y-4" data-id="element-816">
                          <div data-id="element-817">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-818">
                              Nombre Completo
                            </label>
                            <div className="relative" data-id="element-819">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-820">
                                <User className="h-5 w-5 text-gray-400" data-id="element-821" />
                              </div>
                              <input type="text" id="name" required value={contactForm.name} onChange={e => setContactForm({
                          ...contactForm,
                          name: e.target.value
                        })} className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-3 border" placeholder="Ej. Juan Pérez" data-id="element-822" />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-823">
                            <div data-id="element-824">
                              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-825">
                                Correo Electrónico
                              </label>
                              <div className="relative" data-id="element-826">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-827">
                                  <Mail className="h-5 w-5 text-gray-400" data-id="element-828" />
                                </div>
                                <input type="email" id="email" required value={contactForm.email} onChange={e => setContactForm({
                            ...contactForm,
                            email: e.target.value
                          })} className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-3 border" placeholder="juan@ejemplo.com" data-id="element-829" />
                              </div>
                            </div>
                            <div data-id="element-830">
                              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-831">
                                Teléfono / WhatsApp
                              </label>
                              <div className="relative" data-id="element-832">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-833">
                                  <Phone className="h-5 w-5 text-gray-400" data-id="element-834" />
                                </div>
                                <input type="tel" id="phone" required value={contactForm.phone} onChange={e => setContactForm({
                            ...contactForm,
                            phone: e.target.value
                          })} className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-3 border" placeholder="+503 7000-0000" data-id="element-835" />
                              </div>
                            </div>
                          </div>

                          <div data-id="element-836">
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-837">
                              Mensaje Adicional (Opcional)
                            </label>
                            <div className="relative" data-id="element-838">
                              <div className="absolute top-3 left-3 pointer-events-none" data-id="element-839">
                                <MessageSquare className="h-5 w-5 text-gray-400" data-id="element-840" />
                              </div>
                              <textarea id="message" rows={4} value={contactForm.message} onChange={e => setContactForm({
                          ...contactForm,
                          message: e.target.value
                        })} className="pl-10 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 py-3 border" placeholder="¿Tienes alguna pregunta específica sobre este modelo?" data-id="element-841" />
                            </div>
                          </div>

                          <button type="submit" disabled={isSubmitting} className={`w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-lg shadow-sm text-lg font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-1 hover:shadow-lg'}`} data-id="element-842">
                            {isSubmitting ? 'Enviando...' : <>
                                <Send className="w-5 h-5 mr-2" data-id="element-843" />
                                Solicitar Cotización
                              </>}
                          </button>

                          <p className="text-xs text-center text-gray-500 mt-4" data-id="element-844">
                            Al enviar este formulario, aceptas ser contactado
                            por EARIA para dar seguimiento a tu solicitud.
                          </p>
                        </form>
                      </div>}
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons for filter steps */}
              {genericHeaderSteps.includes(step) && <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between" data-id="element-845">
                  {step !== 'budget' ? <button onClick={prevStep} className="flex items-center text-gray-600 font-medium hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-846">
                      <ChevronLeft className="w-5 h-5 mr-1" data-id="element-847" /> Anterior
                    </button> : <div data-id="element-848"></div>}
                  <button onClick={nextStep} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg shadow-blue-600/20 flex items-center transition-all transform hover:-translate-y-0.5" data-id="element-849">
                    {step === 'makes' ? 'Ver Resultados' : 'Siguiente'}
                    <ChevronRight className="w-5 h-5 ml-1" data-id="element-850" />
                  </button>
                </div>}

              {/* Back buttons for selection steps */}
              {(step === 'selectYear' || step === 'trim' || step === 'contact') && <div className="mt-10 pt-6 border-t border-gray-100" data-id="element-851">
                  <button onClick={prevStep} className="flex items-center text-gray-600 font-medium hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-852">
                    <ChevronLeft className="w-5 h-5 mr-1" data-id="element-853" /> Volver
                  </button>
                </div>}

              {step === 'results' && <div className="mt-10 text-center" data-id="element-854">
                  <button onClick={() => {
                setFilters(INITIAL_FILTERS);
                setStep('budget');
              }} className="text-gray-500 hover:text-gray-900 font-medium underline" data-id="element-855">
                    Realizar otra búsqueda
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </div>
      <Footer data-id="element-856" />
    </div>;
}